import { type Instant, parseInstant } from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, principalKey } from '../http/keys.ts'

export interface ManagedUser {
  readonly subject: string
  readonly name: string
  readonly banned: boolean
}

export interface UserSession {
  readonly id: string
  readonly userAgent: string | undefined
  readonly ipAddress: string | undefined
  readonly createdAt: Instant
  readonly expiresAt: Instant
}

export const identityKeys = {
  sessions: (subject: string): CacheKey => principalKey('identity', subject, 'sessions'),
  invalidatedByEndSession: (subject: string): Invalidations => [
    principalKey('identity', subject, 'sessions'),
  ],
} as const

export interface IdentityRepository {
  listSessions(subject: string, signal?: AbortSignal): Promise<readonly UserSession[]>
  endSession(subject: string, sessionId: string): Promise<void>
  suspend(subject: string): Promise<ManagedUser>
  restore(subject: string): Promise<ManagedUser>
}

export function createIdentityRepository(client: TalerClient): IdentityRepository {
  const base = (subject: string) => `/api/v1/users/${encodeURIComponent(subject)}`

  return {
    async listSessions(subject, signal) {
      const response = await client.call<Schemas['SessionResponse'][]>({
        method: 'GET',
        path: `${base(subject)}/sessions`,
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toUserSession)
    },

    async endSession(subject, sessionId) {
      await client.call<void>({
        method: 'DELETE',
        path: `${base(subject)}/sessions/${encodeURIComponent(sessionId)}`,
      })
    },

    async suspend(subject) {
      const response = await client.call<Schemas['UserResponse']>({
        method: 'POST',
        path: `${base(subject)}/suspend`,
      })
      return toManagedUser(response)
    },

    async restore(subject) {
      const response = await client.call<Schemas['UserResponse']>({
        method: 'POST',
        path: `${base(subject)}/restore`,
      })
      return toManagedUser(response)
    },
  }
}

function toUserSession(response: Schemas['SessionResponse']): UserSession {
  return {
    id: response.id,
    userAgent: response.userAgent,
    ipAddress: response.ipAddress,
    createdAt: parseInstant(response.createdAt),
    expiresAt: parseInstant(response.expiresAt),
  }
}

function toManagedUser(response: Schemas['UserResponse']): ManagedUser {
  return { subject: response.subject, name: response.name, banned: response.banned }
}
